import { useEffect, useState } from 'react';

export type FeedbackDraft = {
  type: 'bug' | 'feature' | 'ux' | 'fachlich';
  title: string;
  description: string;
  appContext: string;
  screenshot: string;
};
export const emptyFeedbackDraft = (): FeedbackDraft => ({
  type: 'bug', title: '', description: '', appContext: '', screenshot: '',
});
const hasContent = (f: FeedbackDraft) => !!(f.title || f.description || f.appContext || f.screenshot || f.type !== 'bug');
const errorCause = (error: unknown) => error instanceof Error ? error.message : String(error);

type SaveResult = { ok: true } | { ok: false; cause: string };

/** Storage failure is an explicit result, never an empty/successful draft.
 * Keep the user's unsent text in memory and surface the cause beside it.
 */
function saveDraft(key: string, form: FeedbackDraft): SaveResult {
  try {
    if (hasContent(form)) sessionStorage.setItem(key, JSON.stringify(form));
    else sessionStorage.removeItem(key);
    return { ok: true };
  } catch (error) {
    return { ok: false, cause: errorCause(error) };
  }
}

/** Window-local drafts survive panel unmount/reload, never cross account identities.
 * The caller must remount this hook when the identity changes (keyed panel).
 */
export function useFeedbackDraft(userId?: string) {
  const key = userId ? `cui:feedback-draft:v1:${userId}` : null;
  const [initial] = useState(() => {
    try {
      const raw = key ? sessionStorage.getItem(key) : null;
      if (!raw) return { form: emptyFeedbackDraft(), error: '' };
      const parsed = JSON.parse(raw);
      if (!parsed || !['bug', 'feature', 'ux', 'fachlich'].includes(parsed.type)
        || !['title', 'description', 'appContext', 'screenshot'].every(k => typeof parsed[k] === 'string')) {
        throw new Error('Ungültiges Entwurfsformat');
      }
      return { form: parsed as FeedbackDraft, error: '' };
    } catch (error) {
      return { form: emptyFeedbackDraft(), error: `Feedback-Entwurf konnte nicht wiederhergestellt werden: ${errorCause(error)}. Der gespeicherte Eintrag wird bei der nächsten erfolgreich gesicherten Eingabe ersetzt.` };
    }
  });
  const [form, setForm] = useState(initial.form);
  const [draftError, setDraftError] = useState(initial.error);
  useEffect(() => {
    if (!key || form === initial.form) return;
    const result = saveDraft(key, form);
    setDraftError(result.ok ? '' : `Feedback-Entwurf konnte nicht gesichert werden: ${result.cause}. Bitte dieses Fenster geöffnet lassen und den Text vor einem Wechsel kopieren.`);
  }, [form, initial, key]);
  return { form, setForm, draftError, restored: hasContent(initial.form) };
}
